import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from "prop-types";

import { noop } from 'utils/common';
import useConflictResolver from "hooks/useConflictResolver";

import SingleFileModal from './SingleFileModal';

import SkipMultipleModal from './SkipMultipleModal';
import RenameSingleModal from './RenameSingleModal';
import OverwriteMultipleModal from './OverwriteMultipleModal';
import RenameMultipleModal from './RenameMultipleModal';

import './UploadErrorModal.css';

const modalsByAction = {
	skipMultiple: SkipMultipleModal,
	promptRename: RenameSingleModal,
	renameMultiple: RenameMultipleModal,
	overwriteMultiple: OverwriteMultipleModal,
};

export default function UploadErrorModal(props) {
	const {
		libraryId,
		conflicts,

		namesInUse,

		onCancel,
		onCommit,
	} = props;

	const resolver = useConflictResolver(conflicts.map(conflict => ({ ...conflict, name: conflict.filename })));

	const [previousNames, setPreviousNames] = useState(namesInUse); /*
		Because we don't upload anything until all conflicts have been resolved by the user,
		it's possible to set a new name for a conflicted file, and then attempt to set the same
		name for a later conflicted file.

		This needs to be prevented by form validators, so we have to provide this list to each modal,
		which can be combined with the remaining `name`s from `conflicts`, which is also passed.

		We initialize this with `props.namesInUse`, i.e. the other filenames in this upload batch that
		weren't conflicted with names on the server.
	*/

	const [currentIndex, setCurrentIndex] = useState(0)
		, current = conflicts?.[currentIndex]
		, { filename, approval } = current
		, multiErrorTitle = `Upload Error ${currentIndex + 1} of ${conflicts.length} - ${filename}`
	 	, title = conflicts.length > 1 ? multiErrorTitle : `Upload Error ${filename}`
	;
	const canApplyToAll = currentIndex < (conflicts.length - 1);

	const [action, setAction] = useState(null); /*
		When `action` is null, we default to viewing one conflict at a time, prompting for how the
		user would like to resolve it.

		When we need further input, like new names, or to confirm applying an action to all remaining
		conflicts, we set the `action`, which dictates what modal to show.
	*/

	const unresolvedConflicts = useMemo(() => {
		/*
			When the user selects "Apply to All", it means apply to all *remaining* conflicts that they
			haven't already specified resolutions for, so we have to separate them.
		*/
		return conflicts.slice(currentIndex);
	}, [conflicts, currentIndex]);

	const [isCommitted, setIsCommitted] = useState(false);
	function commitResolutions(){
		setIsCommitted(true);
	}
	useEffect(() => {
		/*
			This is set up as an effect because we need to wait for state to update before we can
			send the final resolutions state back to the uploader.
		*/
		if(isCommitted){
			onCommit({
				resolutions: resolver.resolutions,
				shouldOverwrite: true,
			});
		}
	}, [isCommitted]);

	const incrementIndex = (resolvedName = current.filename) => {
		setPreviousNames([...previousNames, resolvedName]);
		if(currentIndex < conflicts.length -1) {
			setCurrentIndex(currentIndex + 1);
		} else {
			// We've iterated through all conflicts and can exit this flow
			commitResolutions();
		}
	};


	function onSkip(applyToAll){
		if(applyToAll){
			// Prompt for confirmation
			setAction("skipMultiple");
		} else {
			// Commit skipping a single file
			resolver.skip([current.filename]);
			incrementIndex();
		}
	}

	function onConfirmSkip(){
		// Commit skipping the remaining files and exit the flow
		let names = Object.values(unresolvedConflicts).map(conflict => conflict.filename);
		resolver.skip(names);
		commitResolutions();
	}


	function onSingleRename(newName){
		// Commit a new name for a single file
		setAction(null);
		resolver.rename([
			[current.filename, newName]
		]);
		incrementIndex(newName);
	}

	function onMultipleRename(values){
		// Commit new names for the remaining files and exit the flow
		let namePairs = Object.values(values).map(({ original, newName }) => {
			return [original, newName];
		});
		resolver.rename(namePairs);
		commitResolutions();
	}

	function onRename(applyToAll){
		// Both modes here start prompts for new name(s)
		if(applyToAll){
			setAction("renameMultiple");
		} else {
			setAction("promptRename");
		}
	}


	function onOverwrite(applyToAll){
		if(applyToAll){
			// Prompt for confirmation
			setAction("overwriteMultiple");
		} else {
			// Commit overwriting a single file
			resolver.overwrite([current.filename]);
			incrementIndex();
		}
	}

	function onConfirmOverwrite(){
		/*
			Commit overwriting the remaining overwritable files and exit the flow.

			Conflicts default to skip, so we just filter out the ones that we can't overwrite,
			and mark the rest to overwrite.
		*/
		let names = Object.values(unresolvedConflicts)
			.filter(conflict => !(["APPROVED", "SUBMITTED"].includes(conflict.approval)))
			.map(conflict => conflict.filename)
		;
		resolver.overwrite(names);
		commitResolutions();
	}


	function onBack(){
		// Go back to the single conflict view
		setAction(null);
	}

	const ActionModal = modalsByAction[action] || SingleFileModal;
	const modalProps = {
		libraryId,

		title,
		filename,
		approval,
		conflicts: unresolvedConflicts,
		previousNames,
		canApplyToAll,

		onSkip,
		onConfirmSkip, // Skipping the remaining conflicts

		onSingleRename,
		onMultipleRename,
		onRename,

		onOverwrite,
		onConfirmOverwrite, // Overwriting the remaining conflicts

		onCancel, // Canceling out of entire upload flow
		onBack, // Not confirming a bulk action, returning to single view
	};

	return <ActionModal {...modalProps} />;
}

UploadErrorModal.displayName = "CreativeUploadErrorModal";

UploadErrorModal.propTypes = {
	libraryId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]).isRequired,

	conflicts: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		fileName: PropTypes.string,
		approval: PropTypes.string,
	})).isRequired,

	namesInUse: PropTypes.arrayOf(PropTypes.string),

	onCancel: PropTypes.func,
	onUpload: PropTypes.func,
};

UploadErrorModal.defaultProps = {
	onCancel: noop,
	onCommit: noop,
};
